import { Stack, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../assets/styles/tokens";
import { Box } from "@mui/system";
import RefreshIcon from "@mui/icons-material/Refresh";
import { type OutputMetaField } from "../../../../../helpers/bizDevelopmentDashboardHelpers";
import {
  type CommittedSelection,
  getCompareLabel,
  getPrimaryLabel,
} from "../../../../../helpers/timeFrameCompareState";

import PerformanceSortBy from "./PerformanceSortBy";
import PerformanceDateTimeDrawer from "./PerformanceDateTimeDrawer";

interface PerformanceDateFilterProps {
  committed: CommittedSelection;
  onApply: (next: CommittedSelection) => void;
  hasData: boolean;
  isApiLoading: boolean;
  onRefresh: () => void;
  onDownload: () => void;
  brandCount: number;
  isMtd: boolean;
  isCustom: boolean;
  isComparisonActive: boolean;
  sortKey: string;
  sortDir: "asc" | "desc";
  onSortApply: (key: string, dir: "asc" | "desc") => void;
  outputMetadata: Record<string, OutputMetaField>;
}

const PerformanceDateFilter = ({
  committed,
  onApply,
  hasData,
  isApiLoading,
  onRefresh,
  onDownload,
  brandCount,
  isMtd,
  isCustom,
  isComparisonActive,
  sortKey,
  sortDir,
  onSortApply,
  outputMetadata,
}: PerformanceDateFilterProps) => {
  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: spacing.Spacing.xs.$value }}
      >
        <PerformanceDateTimeDrawer
          value={committed}
          onApply={onApply}
          hasData={hasData}
          disabled={isApiLoading}
        />
        <Box>
          <IconButton
            data-tracker="811"
            color="secondary"
            size="large"
            onClick={onRefresh}
          >
            <RefreshIcon sx={{ color: colors.GrayLightMode[700].$value }} />
          </IconButton>
        </Box>
      </Stack>

      {isComparisonActive && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {!isMtd && !isCustom && (
            <>
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: spacing.Spacing.lg.$value,
                  height: spacing.Spacing.lg.$value,
                  borderRadius: spacing.Radius.xs.$value,
                  background: colors.GrayLightMode[950].$value,
                }}
              />
              <Typography
                sx={{
                  fontSize: typography.Size.Text.xxs.$value,
                  color: "#000000DE",
                }}
              >
                {getPrimaryLabel(
                  committed.primary,
                  committed.customStart,
                  committed.customEnd,
                )}
              </Typography>
            </>
          )}
          <Box
            component="span"
            sx={{
              display: "inline-block",
              width: spacing.Spacing.lg.$value,
              height: spacing.Spacing.lg.$value,
              borderRadius: spacing.Radius.xs.$value,
              background: colors.GrayLightMode[500].$value,
              ml:
                !isMtd && !isCustom
                  ? spacing.Spacing.lg.$value + "!important"
                  : undefined,
            }}
          />
          <Typography
            sx={{
              fontSize: typography.Size.Text.xxs.$value,
              color: "#000000DE",
            }}
          >
            {getCompareLabel(committed.compare)}
          </Typography>
        </Stack>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mt: spacing.Spacing.md.$value,
          color: colors.GrayLightMode[900].$value,
        }}
      >
        <Box>
          <Typography
            display={"flex"}
            alignItems={"center"}
            flexDirection={"row"}
            gap={1}
            sx={{
              fontSize: typography.Size.Text.xs.$value,
              fontWeight: typography.Font_weight.Regular.$value,
            }}
          >
            Sort by:{" "}
            <PerformanceSortBy
              sortKey={sortKey}
              sortDir={sortDir}
              onApply={onSortApply}
              outputMetadata={outputMetadata}
              isMtd={isMtd}
            />
          </Typography>
          <Typography
            sx={{
              fontSize: typography.Size.Text.xs.$value,
              mt: spacing.Spacing.sm.$value,
            }}
          >
            {brandCount} Brands
          </Typography>
        </Box>
        <IconButton data-tracker="810" color="secondary" size="large" onClick={onDownload}>
          <FileDownloadIcon sx={{ color: colors.GrayLightMode[700].$value }} />
        </IconButton>
      </Stack>
    </>
  );
};

export default PerformanceDateFilter;
